import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import type { AssetManagerStateType } from '../context';
import { anyFilterIsActive, getDefaultFilters } from './assetFilters';

/**
 * Serialization of asset manager state to/from the URL, so filter setups can
 * be shared and survive reloads. The write path prunes anything that matches
 * defaults (generatePrunedClone → encodeAssetManagerState); the read path
 * backfills those defaults again (readAssetManagerStateFromUrl).
 */

export const ASSET_MANAGER_STATE_URL_KEY = 'assetManagerState';

/**
 * The pre-assetManagerAssetTypes format kept the selected asset type as a
 * `typeFilter` key inside a single flat `filters` object per manager.
 */
const convertOldVersionToNewVersion = (data): AssetManagerStateType => Object.keys(data).reduce(
  (acc, key) => {
    const { typeFilter } = data[key].filters || {};
    return {
      ...acc,
      [key]: {
        selectedAssetTypeId: typeFilter,
        assetManagerAssetTypes: {
          [typeFilter]: data[key],
        },
      },
    };
  },
  {},
);

// The URL param is pruned before serialization (generatePrunedClone), so the
// filters it carries are partial. Backfill the missing keys with defaults so
// consumers always see a complete AssetFilter. Merge is shallow on purpose:
// incoming values (including revived dayjs objects) must win untouched.
export const mergeWithDefaultFilters = (
  data: AssetManagerStateType,
): AssetManagerStateType => Object
  .keys(data).reduce((acc, managerId) => {
    const { assetManagerAssetTypes } = data[managerId] || {};
    return {
      ...acc,
      [managerId]: {
        ...data[managerId],
        assetManagerAssetTypes: Object.keys(assetManagerAssetTypes || {}).reduce(
          (assetTypesAcc, assetTypeId) => ({
            ...assetTypesAcc,
            [assetTypeId]: {
              ...assetManagerAssetTypes[assetTypeId],
              filters: {
                ...getDefaultFilters(),
                ...assetManagerAssetTypes[assetTypeId]?.filters,
              },
            },
          }),
          {},
        ),
      },
    };
  }, {});

export const readAssetManagerStateFromUrl = (
  search: string = window.location.search,
): AssetManagerStateType | undefined => {
  const rawValue = new URLSearchParams(search).get(ASSET_MANAGER_STATE_URL_KEY);
  if (!rawValue) return undefined;
  // URLSearchParams.get has already percent-decoded once. URLs written before
  // the double-encoding fix carry a second layer; state JSON always starts
  // with '{', so a value still starting with '%' is legacy double-encoded.
  const jsonString = rawValue.startsWith('%') ? decodeURIComponent(rawValue) : rawValue;
  const custom = (k: string, v: any) => {
    switch (k) {
      case 'dateFilter':
        return (v[0] && v[1]) ? [dayjs(v[0]), dayjs(v[1])] : [];
      case 'timeTravelDate':
        return v && dayjs(v);
      default:
        return v;
    }
  };

  // Hand-edited or truncated shared URLs shouldn't crash app boot.
  try {
    const data = JSON.parse(jsonString, custom);

    const isOldVersion = !!(
      Object.keys(data).length > 0
      && Object.keys(data)[0]
      && data[Object.keys(data)[0]]?.filters
    );

    return mergeWithDefaultFilters(isOldVersion ? convertOldVersionToNewVersion(data) : data);
  } catch {
    return undefined;
  }
};

/**
 * Deep-clones the state and strips everything that is session-only
 * (assetSelection, lastFilterUpdate) or indistinguishable from defaults, so
 * the URL only carries meaningful deviations. Note the clone stringifies
 * dayjs values to ISO strings; readAssetManagerStateFromUrl revives them.
 */
export const generatePrunedClone = (
  state: AssetManagerStateType,
  defaultAssetTypeId: string,
  defaultSelectedPlanId: string | undefined,
): AssetManagerStateType => {
  const clone = JSON.parse(JSON.stringify(state)) as AssetManagerStateType;
  Object.keys(clone).forEach((k) => {
    const { assetManagerAssetTypes, selectedAssetTypeId } = clone[k];

    Object.keys(assetManagerAssetTypes || {}).forEach((assetTypeId) => {
      const assetManagerAssetType = assetManagerAssetTypes?.[assetTypeId] || {};
      const {
        filters, collapsable, selectedPlanId, showPlanView,
      } = assetManagerAssetType;

      // @ts-ignore
      delete clone[k].assetManagerAssetTypes?.[assetTypeId]?.assetSelection;

      // Session-only timestamp; don't persist it as a string and break
      // getMaxDate (.getTime()) on rehydration.
      delete clone[k].assetManagerAssetTypes?.[assetTypeId]?.filters?.lastFilterUpdate;

      const isFilterActive = anyFilterIsActive(
        filters,
        defaultAssetTypeId,
        selectedAssetTypeId,
      );

      const noCollapsableActive = Object
        .values(
          collapsable || {},
        )?.every(v => v.isCollapsed === false && v.showAll === false);

      if (noCollapsableActive) {
        delete clone[k].assetManagerAssetTypes?.[assetTypeId]?.collapsable;
      }

      if (filters && !isFilterActive) {
        // @ts-ignore
        delete clone[k].assetManagerAssetTypes?.[assetTypeId]?.filters;
      }

      if (selectedPlanId === defaultSelectedPlanId) {
        // @ts-ignore
        delete clone[k].assetManagerAssetTypes?.[assetTypeId]?.selectedPlanId;
      }

      if (!showPlanView) {
        // @ts-ignore
        delete clone[k].assetManagerAssetTypes?.[assetTypeId]?.showPlanView;
      }

      if (isEmpty(assetManagerAssetType)) {
        delete clone[k].assetManagerAssetTypes?.[assetTypeId];
      }
    });

    if (isEmpty(assetManagerAssetTypes)) {
      delete clone[k];
    }
  });
  return clone;
};

/**
 * Returns the URL param value for a pruned clone, or null when there is
 * nothing worth persisting (the param should be removed). Plain JSON — the
 * caller sets it via URLSearchParams, which percent-encodes on serialization;
 * encoding here as well is what produced double-encoded URLs historically.
 */
export const serializeAssetManagerState = (clone: AssetManagerStateType): string | null => (
  isEmpty(clone) ? null : JSON.stringify(clone)
);
